import React, { useState, useEffect, useCallback } from 'react';
import { FormInput, FormSelect, FormTextArea } from '../../forms';
import { FileText, AlertCircle, CheckCircle, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../services/api';
import { debounce } from "lodash";
import { useAuth } from '../../../hooks/useAuth';
import {secureDraftService} from "../../../services/draft.service"

const CounselingRecordForm = ({ onSuccess, handleFormChange, hasUnsavedChanges, lastAutoSave }) => {
  const { appointmentId, sessionId: urlSessionId } = useParams();
  const [sessionId, setSessionId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Local draft state
  const [localHasUnsavedChanges, setLocalHasUnsavedChanges] = useState(false);
  const [localLastAutoSave, setLocalLastAutoSave] = useState(null);
  
  const [formData, setFormData] = useState({
    sessionDate: new Date().toISOString().split('T')[0],
    sessionNumber: '',
    initialDiagnosis: '',
    presentingIssues: '',
    goalsDiscussed: '',
    interventionsUsed: [],
    progress: '',
    planForNextSession: '',
    homework: '',
    prognosis: '',
    followUpDate: ''
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();

  // Initialize draft service
  useEffect(() => {
    if (user?.id && user?.token) {
      secureDraftService.initialize(user.id, user.token);
      console.log('Draft service initialized for user:', user.id);
    }
  }, [user]);

  // Auto-save function with enhanced logging
  const autoSaveDraft = useCallback(
    debounce((formData, sessionId) => {
      console.log('autoSaveDraft called with:', { 
        sessionId, 
        userId: user?.id, 
        formDataKeys: Object.keys(formData),
        hasFormData: Object.keys(formData).some(key => formData[key] && formData[key] !== '')
      });
      
      if (sessionId && user?.id && formData) {
        // Set local state immediately
        setLocalHasUnsavedChanges(true);
        
        // Call the draft service
        secureDraftService.autoSaveDraft(sessionId, 'record', formData);
        
        // Update local save time after a short delay
        setTimeout(() => {
          setLocalLastAutoSave(new Date());
          setLocalHasUnsavedChanges(false);
        }, 500);
        
        console.log('Draft auto-save triggered for record');
      } else {
        console.warn('autoSaveDraft: Missing required parameters', { sessionId, userId: user?.id, hasFormData: !!formData });
      }
    }, 3000),
    [user?.id]
  );

  // Load existing draft when sessionId is available
  const loadExistingDraft = useCallback(() => {
    if (sessionId && user?.id) {
      console.log('Loading existing draft for session:', sessionId);
      const draftData = secureDraftService.getDraft(sessionId, 'record');
      if (draftData) {
        console.log('Found existing draft data:', Object.keys(draftData));
        setFormData(prev => ({
          ...prev,
          ...draftData
        }));
        setLocalHasUnsavedChanges(true);
        console.log('Loaded existing draft for record');
      } else {
        console.log('No existing draft found for session:', sessionId);
      }
    }
  }, [sessionId, user?.id]);

  // Handle form field changes with auto-save
  const handleChange = (field, value) => {
    console.log('Field changed:', field, 'Value:', value);
    
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    setLocalHasUnsavedChanges(true);
    
    // Use parent's handleFormChange if available, otherwise use local auto-save
    if (handleFormChange) {
      handleFormChange('record', updatedData);
    } else {
      // Make sure we have sessionId before auto-saving
      if (sessionId) {
        console.log('Triggering auto-save for field change:', field);
        autoSaveDraft(updatedData, sessionId);
      } else {
        console.warn('Cannot auto-save: sessionId not available yet');
      }
    }
  };

  useEffect(() => {
    const initializeOrFetchSession = async () => {
      try {
        setLoading(true);
        
        // If we have a direct sessionId in the URL, we're likely editing an existing record
        if (urlSessionId) {
          console.log('Using URL sessionId:', urlSessionId);
          setSessionId(urlSessionId);
          setIsEditMode(true);
          
          // Fetch the existing counseling record for this session
          const recordResponse = await api.get(`/sessions/${urlSessionId}/record`);
          
          if (recordResponse?.data?.status === "success" && recordResponse.data.data.record) {
            const recordData = recordResponse.data.data.record;
            
            const formDataFromRecord = {
              sessionDate: recordData.sessionDate ? recordData.sessionDate.split('T')[0] : new Date().toISOString().split('T')[0],
              sessionNumber: recordData.sessionNumber || '',
              initialDiagnosis: recordData.initialDiagnosis || '',
              presentingIssues: recordData.presentingIssues || '',
              goalsDiscussed: recordData.goalsDiscussed || '',
              interventionsUsed: recordData.interventionsUsed || [],
              progress: recordData.progress || '',
              planForNextSession: recordData.planForNextSession || '',
              homework: recordData.homework || '',
              prognosis: recordData.prognosis || '',
              followUpDate: recordData.followUpDate ? recordData.followUpDate.split('T')[0] : ''
            };
            
            setFormData(formDataFromRecord);
            
            // Load draft after setting initial data
            setTimeout(() => {
              loadExistingDraft();
            }, 100);
          } else {
            setError("No counseling record found for this session");
          }
        } 
        // If we have an appointmentId, this is either a new record or we need to check for existing
        else if (appointmentId) {
          console.log('Using appointmentId:', appointmentId);
          // Check if there's already a session for this appointment
          try {
            const sessionResponse = await api.get(`/appointments/${appointmentId}/session`);
            
            if (sessionResponse?.data?.status === "success" && sessionResponse?.data?.data) {
              // Session exists
              const existingSessionId = sessionResponse.data.data._id;
              console.log("Found existing session:", existingSessionId);
              setSessionId(existingSessionId);
              
              // Check if there's an existing counseling record
              const recordResponse = await api.get(`/sessions/${existingSessionId}`);
           
              if (recordResponse?.data?.status === "success" && recordResponse?.data?.data?.record) {
                // We have an existing record - set edit mode
                setIsEditMode(true);
                const recordData = recordResponse.data.data.record;
                console.log("Found existing counseling record data");
                
                const formDataFromRecord = {
                  sessionDate: recordData.sessionDate ? recordData.sessionDate.split('T')[0] : new Date().toISOString().split('T')[0],
                  sessionNumber: recordData.sessionNumber || '',
                  initialDiagnosis: recordData.initialDiagnosis || '',
                  presentingIssues: recordData.presentingIssues || '',
                  goalsDiscussed: recordData.goalsDiscussed || '',
                  interventionsUsed: recordData.interventionsUsed || [],
                  progress: recordData.progress || '',
                  planForNextSession: recordData.planForNextSession || '',
                  homework: recordData.homework || '',
                  prognosis: recordData.prognosis || '',
                  followUpDate: recordData.followUpDate ? recordData.followUpDate.split('T')[0] : ''
                };
                
                setFormData(formDataFromRecord);
              }
              
              // Load draft after setting session ID
              setTimeout(() => {
                loadExistingDraft();
              }, 100);
            } else {
              // No session exists yet, initialize a new one
              console.log('Creating new session for appointment:', appointmentId);
              const initResponse = await api.post(`/sessions/${appointmentId}/initialize`);
              const newSessionId = initResponse.data.data._id;
              console.log('Created new session:', newSessionId);
              setSessionId(newSessionId);
              setIsEditMode(false);
              
              // Load draft after setting session ID
              setTimeout(() => {
                loadExistingDraft();
              }, 100);
            }
          } catch (sessionErr) {
            // If error fetching session, initialize a new one
            console.log('Error fetching session, creating new one:', sessionErr.message);
            const initResponse = await api.post(`/sessions/${appointmentId}/initialize`);
            const newSessionId = initResponse.data.data._id;
            console.log('Created new session after error:', newSessionId);
            setSessionId(newSessionId);
            setIsEditMode(false);
            
            // Load draft after setting session ID
            setTimeout(() => {
              loadExistingDraft();
            }, 100);
          }
        } else {
          setError("No appointment or session ID provided");
        }
      } catch (err) {
        console.error("Error initializing or fetching session:", err);
        setError("Failed to initialize or fetch session data");
      } finally {
        setLoading(false);
      }
    };

    initializeOrFetchSession();
  }, [appointmentId, urlSessionId, loadExistingDraft]);

  // Effect to trigger auto-save when sessionId becomes available and we have form data
  useEffect(() => {
    if (sessionId && user?.id) {
      console.log('SessionId and userId available, checking for auto-save trigger');
      // Check if we have any meaningful form data to save
      const hasData = Object.keys(formData).some(key => 
        formData[key] && formData[key] !== '' && 
        (Array.isArray(formData[key]) ? formData[key].length > 0 : true)
      );
      
      if (hasData) {
        console.log('Form has data, triggering initial auto-save');
        autoSaveDraft(formData, sessionId);
      }
    }
  }, [sessionId, user?.id, autoSaveDraft]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) {
      console.error("No session ID available!");
      setError("Session ID is missing. Please try again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!formData.sessionDate || !formData.sessionNumber ||
        !formData.presentingIssues || !formData.progress ||
        !formData.planForNextSession || !formData.followUpDate) {
        throw new Error('Please fill in all required fields');
      }

      const payload = {
        ...formData,
        sessionDate: formData.sessionDate || new Date().toISOString(),
      };

      let response;
      
      if (isEditMode) {
        // Update existing record
        response = await api.put(`/sessions/${sessionId}/update-record`, payload);
      } else {
        // Create new record
        response = await api.post(`/sessions/${appointmentId}/create-record`, payload);
      }

      if (response?.data?.status === "success") {
        // Clear the draft after successful submission
        secureDraftService.clearDraft(sessionId, 'record');
        setLocalHasUnsavedChanges(false);
        
        setSuccess(true);
        
        // Complete the session
        try {
          const completeResponse = await api.post(`/sessions/${sessionId}/complete`);
          
          if (completeResponse?.data?.status !== "success") {
            console.error("Session completion error:", completeResponse);
          }
        } catch (completeErr) {
          console.error("Session completion error:", completeErr);
        }
        
        // If onSuccess callback provided, call it
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(response.data);
        }
        
        // Short delay before navigation
        setTimeout(() => {
          if (user?.role === "hod") {
            navigate("/hod/sessions");
          } else {
            navigate("/counselor/sessions");
          }
        }, 1500);
      }
    } catch (err) {
      console.error("Counseling record error:", err);
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'save'} counseling record`);
    } finally {
      setLoading(false);
    }
  };



  // Use parent props if available, otherwise use local state
  const currentHasUnsavedChanges = hasUnsavedChanges !== undefined ? hasUnsavedChanges : localHasUnsavedChanges;
  const currentLastAutoSave = lastAutoSave !== undefined ? lastAutoSave : localLastAutoSave;
  
  console.log("Auto-save status:", { 
    currentHasUnsavedChanges, 
    currentLastAutoSave, 
    sessionId, 
    userId: user?.id 
  });
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 mb-2">
            Counseling Record {isEditMode ? 'Updated' : 'Saved'} Successfully
          </h2>
          <p className="text-gray-600">Redirecting you to all sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with draft status */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          {isEditMode ? 'Edit Counseling Record' : 'New Counseling Record'}
        </h2>
        
        {/* Auto-save status */}
        <div className="flex items-center gap-2">
          {currentHasUnsavedChanges && (
            <div className="bg-amber-50 px-3 py-1 rounded text-amber-700 flex items-center gap-1">
              <Save className="h-4 w-4 animate-pulse" />
              <span>Auto-saving...</span>
            </div>
          )}
          
          {currentLastAutoSave && !currentHasUnsavedChanges && (
            <div className="bg-green-50 px-3 py-1 rounded text-green-700 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              <span>Saved {new Date(currentLastAutoSave).toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormInput
            type="date"
            label="Session Date"
            value={formData.sessionDate}
            onChange={(e) => handleChange('sessionDate', e.target.value)}
            required
          />

          <FormInput
            type="number"
            label="Session Number"
            value={formData.sessionNumber}
            onChange={(e) => handleChange('sessionNumber', e.target.value)}
            required
          />
        </div>

        <FormTextArea
          label="Initial Diagnosis"
          value={formData.initialDiagnosis}
          onChange={(e) => handleChange('initialDiagnosis', e.target.value)}
          rows={4}
          required
        />

        <FormTextArea
          label="Current session presenting issue"
          value={formData.presentingIssues}
          onChange={(e) => handleChange('presentingIssues', e.target.value)}
          rows={4}
          required
        />

        <FormTextArea
          label="Counseling Goal"
          value={formData.goalsDiscussed}
          onChange={(e) => handleChange('goalsDiscussed', e.target.value)}
          rows={4}
          required
        />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Current Session Intervention</h3>
          <div className="space-y-3">
          {['CBT', 'Solution Focused', 'Mindfulness', 'Motivational Interviewing', 'Couple Therapy', 'Family Counseling'].map(intervention => {
              const isSelected = Array.isArray(formData.interventionsUsed) 
                ? formData.interventionsUsed.some(i => i.name === intervention)
                : false;
                
              const currentNote = isSelected
                ? formData.interventionsUsed.find(i => i.name === intervention)?.note || ''
                : '';
            return (
            <div key={intervention} className="border rounded-md p-3 bg-gray-50">
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id={`record-${intervention}`}
                  checked={isSelected}
                  onChange={() => {
                        let newInterventions;
                        if (isSelected) {
                          newInterventions = formData.interventionsUsed.filter(i => i.name !== intervention);
                        } else {
                          newInterventions = [...(Array.isArray(formData.interventionsUsed) ? formData.interventionsUsed : []), { name: intervention, note: '' }];
                        }
                        handleChange('interventionsUsed', newInterventions);
                      }}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor={`record-${intervention}`} className="ml-2 text-sm text-gray-700">
                  {intervention}
                </label>
              </div>
               {isSelected && (
                    <div className="ml-6">
                      <FormInput
                        placeholder="Add specific notes for this intervention..."
                        value={currentNote}
                        onChange={(e) => {
                          const newInterventions = formData.interventionsUsed.map(i => 
                            i.name === intervention ? { ...i, note: e.target.value } : i
                          );
                          handleChange('interventionsUsed', newInterventions);
                        }}
                        className="text-sm"
                      />
                    </div>
                  )}
            </div>
            )
          })}
          </div>
        </div>

        <FormTextArea
          label="Progress Made"
          value={formData.progress}
          onChange={(e) => handleChange('progress', e.target.value)}
          rows={4}
          required
        />

        <FormTextArea
          label="Expected Outcome"
          value={formData.planForNextSession}
          onChange={(e) => handleChange('planForNextSession', e.target.value)}
          rows={4}
          required
        />

        <FormTextArea
          label="Homework/Assignments Given"
          value={formData.homework}
          onChange={(e) => handleChange('homework', e.target.value)}
          rows={4}
        />

        <FormTextArea
          label="Prognosis"
          value={formData.prognosis}
          onChange={(e) => handleChange('prognosis', e.target.value)}
          rows={4}
          required
        />

        <FormInput
          type="date"
          label="Date of Next Appointment"
          value={formData.followUpDate}
          onChange={(e) => handleChange('followUpDate', e.target.value)}
          required
        />

        <div className="flex justify-between items-center">
          {/* Draft status */}
          <div className="text-sm text-gray-600">
            {currentHasUnsavedChanges && (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-amber-400 rounded-full mr-2 animate-pulse"></div>
                Changes will be auto-saved in a few seconds...
              </div>
            )}
            {currentLastAutoSave && !currentHasUnsavedChanges && (
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-4 h-4 mr-2" />
                Last saved: {new Date(currentLastAutoSave).toLocaleTimeString()}
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Update Record' : 'Save Record'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CounselingRecordForm;

// const CounselingRecordForm = ({ onSuccess }) => {
//   const { appointmentId, sessionId: urlSessionId } = useParams();
//   const [sessionId, setSessionId] = useState(null);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [success, setSuccess] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [formData, setFormData] = useState({
//     sessionDate: new Date().toISOString().split('T')[0],
//     sessionNumber: '',
//     initialDiagnosis: '',
//     presentingIssues: '',
//     goalsDiscussed: '',
//     interventionsUsed: [],
//     progress: '',
//     planForNextSession: '',
//     homework: '',
//     prognosis: '',
//     followUpDate: ''
//   });
//   const { user } = useAuth();
//   const navigate = useNavigate();

//   useEffect(() => {
//     const initializeOrFetchSession = async () => {
//       try {
//         setLoading(true);
        
//         // If we have a direct sessionId in the URL, we're likely editing an existing record
//         if (urlSessionId) {
//           setSessionId(urlSessionId);
//           setIsEditMode(true);
          
//           // Fetch the existing counseling record for this session
//           const recordResponse = await api.get(`/sessions/${urlSessionId}/record`);
          
//           if (recordResponse?.data?.status === "success" && recordResponse.data.data.record) {
//             const recordData = recordResponse.data.data.record;
            
//             setFormData({
//               sessionDate: recordData.sessionDate ? recordData.sessionDate.split('T')[0] : new Date().toISOString().split('T')[0],
//               sessionNumber: recordData.sessionNumber || '',
//               initialDiagnosis: recordData.initialDiagnosis || '',
//               presentingIssues: recordData.presentingIssues || '',
//               goalsDiscussed: recordData.goalsDiscussed || '',
//               interventionsUsed: recordData.interventionsUsed || [],
//               progress: recordData.progress || '',
//               planForNextSession: recordData.planForNextSession || '',
//               homework: recordData.homework || '',
//               prognosis: recordData.prognosis || '',
//               followUpDate: recordData.followUpDate ? recordData.followUpDate.split('T')[0] : ''
//             });
//           } else {
//             setError("No counseling record found for this session");
//           }
//         } 
//         // If we have an appointmentId, this is either a new record or we need to check for existing
//         else if (appointmentId) {
//           // Check if there's already a session for this appointment
//           try {
//             const sessionResponse = await api.get(`/appointments/${appointmentId}/session`);
            
//             if (sessionResponse?.data?.status === "success" && sessionResponse?.data?.data) {
//               // Session exists
//               const existingSessionId = sessionResponse.data.data._id;
//               setSessionId(existingSessionId);
//               console.log("Session Id", existingSessionId);
//               // Check if there's an existing counseling record
//               const recordResponse = await api.get(`/sessions/${existingSessionId}`);
           
//               if (recordResponse?.data?.status === "success" && recordResponse?.data?.data?.record) {
//                 // We have an existing record - set edit mode
//                 setIsEditMode(true);
//                 const recordData = recordResponse.data.data.record;
//                 console.log("CR data",recordData)
//                 setFormData({
//                   sessionDate: recordData.sessionDate ? recordData.sessionDate.split('T')[0] : new Date().toISOString().split('T')[0],
//                   sessionNumber: recordData.sessionNumber || '',
//                   initialDiagnosis: recordData.initialDiagnosis || '',
//                   presentingIssues: recordData.presentingIssues || '',
//                   goalsDiscussed: recordData.goalsDiscussed || '',
//                   interventionsUsed: recordData.interventionsUsed || [],
//                   progress: recordData.progress || '',
//                   planForNextSession: recordData.planForNextSession || '',
//                   homework: recordData.homework || '',
//                   prognosis: recordData.prognosis || '',
//                   followUpDate: recordData.followUpDate ? recordData.followUpDate.split('T')[0] : ''
//                 });
//               }
//             } else {
//               // No session exists yet, initialize a new one
//               const initResponse = await api.post(`/sessions/${appointmentId}/initialize`);
//               const newSessionId = initResponse.data.data._id;
//               setSessionId(newSessionId);
//               setIsEditMode(false);
//             }
//           } catch (sessionErr) {
//             // If error fetching session, initialize a new one
//             const initResponse = await api.post(`/sessions/${appointmentId}/initialize`);
//             const newSessionId = initResponse.data.data._id;
//             setSessionId(newSessionId);
//             setIsEditMode(false);
//           }
//         } else {
//           setError("No appointment or session ID provided");
//         }
//       } catch (err) {
//         console.error("Error initializing or fetching session:", err);
//         setError("Failed to initialize or fetch session data");
//       } finally {
//         setLoading(false);
//       }
//     };

//     initializeOrFetchSession();
//   }, [appointmentId, urlSessionId]);

//   const handleFormSubmit = async (e) => {
//     e.preventDefault();
//     if (!sessionId) {
//       console.error("No session ID available!");
//       setError("Session ID is missing. Please try again.");
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       if (!formData.sessionDate || !formData.sessionNumber ||
//         !formData.presentingIssues || !formData.progress ||
//         !formData.planForNextSession || !formData.followUpDate) {
//         throw new Error('Please fill in all required fields');
//       }

//       const payload = {
//         ...formData,
//         sessionDate: formData.sessionDate || new Date().toISOString(),
//       };

//       let response;
      
//       if (isEditMode) {
//         // Update existing record
//         response = await api.put(`/sessions/${sessionId}/update-record`, payload);
//       } else {
//         // Create new record
//         response = await api.post(`/sessions/${appointmentId}/create-record`, payload);
//       }

//       if (response?.data?.status === "success") {
//         setSuccess(true);
        
//         // If we're creating a new record, complete the session

//           try {
//             const completeResponse = await api.post(`/sessions/${sessionId}/complete`);
            
//             if (completeResponse?.data?.status !== "success") {
//               console.error("Session completion error:", completeResponse);
//             }
//           } catch (completeErr) {
//             console.error("Session completion error:", completeErr);
//           }
        
        
//         // If onSuccess callback provided, call it
//         if (onSuccess && typeof onSuccess === 'function') {
//           onSuccess(response.data);
//         }
        
//         // Short delay before navigation
//         setTimeout(() => {
//           if (user?.role === "hod") {
//             navigate("/hod/sessions");
//           } else {
//             navigate("/counselor/sessions");
//           }
//         }, 1500);
//       }
//     } catch (err) {
//       console.error("Counseling record error:", err);
//       setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'save'} counseling record`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleInterventionToggle = (intervention) => {
//     setFormData(prev => ({
//       ...prev,
//       interventionsUsed: prev.interventionsUsed.includes(intervention)
//         ? prev.interventionsUsed.filter(i => i !== intervention)
//         : [...prev.interventionsUsed, intervention]
//     }));
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center py-12">
//         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
//       </div>
//     );
//   }

//   if (success) {
//     return (
//       <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
//         <div className="text-center">
//           <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
//           <h2 className="text-2xl font-medium text-gray-900 mb-2">
//             Counseling Record {isEditMode ? 'Updated' : 'Saved'} Successfully
//           </h2>
//           <p className="text-gray-600">Redirecting you to all sessions...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">
//       <div className="flex justify-between items-center">
//         <h2 className="text-xl font-bold text-gray-900">
//           {isEditMode ? 'Edit Counseling Record' : 'New Counseling Record'}
//         </h2>
//       </div>
      
//       {error && (
//         <div className="bg-red-50 border-l-4 border-red-400 p-4">
//           <div className="flex">
//             <AlertCircle className="h-5 w-5 text-red-400" />
//             <div className="ml-3">
//               <p className="text-sm text-red-700">{error}</p>
//             </div>
//           </div>
//         </div>
//       )}

//       <form onSubmit={handleFormSubmit} className="space-y-6">
//         <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
//           <FormInput
//             type="date"
//             label="Session Date"
//             value={formData.sessionDate}
//             onChange={(e) => setFormData(prev => ({
//               ...prev,
//               sessionDate: e.target.value
//             }))}
//             required
//           />

//           <FormInput
//             type="number"
//             label="Session Number"
//             value={formData.sessionNumber}
//             onChange={(e) => setFormData(prev => ({
//               ...prev,
//               sessionNumber: e.target.value
//             }))}
//             required
//           />
//         </div>

//         <FormTextArea
//           label="Initial Diagnosis"
//           value={formData.initialDiagnosis}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             initialDiagnosis: e.target.value
//           }))}
//           rows={4}
//           required
//         />

//         <FormTextArea
//           label="Current session presenting issue"
//           value={formData.presentingIssues}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             presentingIssues: e.target.value
//           }))}
//           rows={4}
//           required
//         />

//         <FormTextArea
//           label="Counseling Goal"
//           value={formData.goalsDiscussed}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             goalsDiscussed: e.target.value
//           }))}
//           rows={4}
//           required
//         />

//         <div className="space-y-2">
//           <h3 className="text-sm font-medium text-gray-900">Current Session Intervention</h3>
//           {['CBT', 'Solution Focused', 'Mindfulness', 'Motivational Interviewing'].map(intervention => (
//             <div key={intervention} className="flex items-center">
//               <input
//                 type="checkbox"
//                 id={intervention}
//                 checked={formData.interventionsUsed.includes(intervention)}
//                 onChange={() => handleInterventionToggle(intervention)}
//                 className="h-4 w-4 text-blue-600 rounded border-gray-300"
//               />
//               <label htmlFor={intervention} className="ml-2 text-sm text-gray-700">
//                 {intervention}
//               </label>
//             </div>
//           ))}
//         </div>

//         <FormTextArea
//           label="Progress Made"
//           value={formData.progress}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             progress: e.target.value
//           }))}
//           rows={4}
//           required
//         />

//         <FormTextArea
//           label="Expected Outcome"
//           value={formData.planForNextSession}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             planForNextSession: e.target.value
//           }))}
//           rows={4}
//           required
//         />

//         <FormTextArea
//           label="Homework/Assignments Given"
//           value={formData.homework}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             homework: e.target.value
//           }))}
//           rows={4}
//         />

//         <FormTextArea
//           label="Prognosis"
//           value={formData.prognosis}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             prognosis: e.target.value
//           }))}
//           rows={4}
//           required
//         />

//         <FormInput
//           type="date"
//           label="Date of Next Appointment"
//           value={formData.followUpDate}
//           onChange={(e) => setFormData(prev => ({
//             ...prev,
//             followUpDate: e.target.value
//           }))}
//           required
//         />

//         <div className="flex justify-end space-x-3">
//           <button
//             type="button"
//             onClick={() => navigate(-1)}
//             className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             disabled={loading}
//             className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
//           >
//             {loading ? (
//               <>
//                 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
//                 {isEditMode ? 'Updating...' : 'Saving...'}
//               </>
//             ) : (
//               <>
//                 <FileText className="w-4 h-4 mr-2" />
//                 {isEditMode ? 'Update Record' : 'Save Record'}
//               </>
//             )}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default CounselingRecordForm;